import React, { useState } from 'react';
import { Box } from '@components/Box';
import ReactHtmlParser from 'react-html-parser';
import { Columns } from '@components/Columns';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { GalleryECardModal } from '@components/GalleryComponents/GalleryECardModal/GalleryECardModal';
import {
  StyledDesignColumns,
  StyledDesignColumn,
  StyledDesignTextColumn,
  StyledDesignTextImageColumn,
  StyledPlaceholderCloudImage,
  StyledTextPlaceholderCloudImage,
  CarouselSlideDetails,
  CtaWrapper,
  SlideDetailsBodyText,
  SlideDetailsText,
  StyledCTALink,
} from './DesignsSection.styles';
import { ButtonNoneStyled } from '@components/ButtonNoneStyled';
const analytics = require('../../../../static/js/joy');
import resources from '@i18n/manifest';
import { useTranslation } from 'react-i18next';
import { WEBSITE_TEMPLATE_GALLERY_CARDS } from '@utils/designTemplates';

interface DesignsSectionProps
  extends Readonly<{
    themes: MarketingData;
    sectionData: typeof resources.weddingWebsitePage.sections.designs;
  }> {}

export const DesignsSection: React.FC<DesignsSectionProps> = ({ sectionData, themes }) => {
  const { t } = useTranslation('galleryPage');
  const productTypes = t('productTypes');

  const sectionId = 'Design_Templates';
  const [isVisible, setIsVisible] = useState(false);
  const [isDisplayCardDetails, setCardDetailModalToggle] = useState(false);
  const { designs } = productTypes;
  const [cardDetails, setCardDetails] = useState<GalleryECard>({
    title: '',
    variationType: '',
    imageUrl: '',
    colors: [''],
    font: '',
    printUrl: '',
    tags: [''],
    vendor: '',
    productType: designs as GalleryProductType,
    variantColor: '',
    variantName: '',
    variantThemeId: '',
    theme: {
      name: '',
      themeId: '',
      marketingData: [],
    },
  });

  const handleCardClick = (search: { themeId?: string; title?: string }, layout: 'brannan' | 'aloha') => {
    let card: GalleryECard | undefined;

    if (search.themeId) {
      card = themes.find((theme) => theme.variantThemeId === search.themeId && theme.variationType === layout);
    }

    if (search.title) {
      card = WEBSITE_TEMPLATE_GALLERY_CARDS.find((card) => card.theme.name === search.title && card.variationType === layout);
    }

    if (typeof card !== 'undefined') {
      analytics.segment.sendTelemetryEvent('designBlockThemeClicked', {
        label: card.variantName ? card.variantName : card.theme?.name,
        value: 2,
        extraInfo: {
          version: 'abtestwwdesincurrent_2',
        },
      });
      setCardDetails(card);
      setCardDetailModalToggle(true);
    }
  };

  const handleCloseModal = () => {
    setCardDetailModalToggle(false);
  };

  return (
    <Box as={'section'} marginTop={[5, 0, 10]} marginBottom={[9, 0]} style={{ position: 'relative', zIndex: 2 }} id={sectionId}>
      <LazyLoadWrapper beforeLoad={() => setIsVisible(true)} threshold={0}>
        <div />
      </LazyLoadWrapper>
      <StyledDesignColumns stackOnMobile={false} centered verticalAlign="center" marginTop={[6, 10]} marginBottom={[6, 6]}>
        <StyledDesignColumn width={3} tablet={'four'} desktop={'four'} mobile="four">
          <ButtonNoneStyled
            id="DesignBohemianEucalyptus"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick({ themeId: 'bohemian_eucalyptus' }, 'brannan')}
          >
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/bohemian_eucalyptus_brannan.png"
              alt="Bohemian Eucalyptus Brannan"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
        <StyledDesignColumn width={3} tablet={'four'} desktop={'four'} mobile="four">
          <ButtonNoneStyled
            id="DesignEmilioMocha"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick({ title: 'Emilio Mocha' }, 'brannan')}
          >
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/emilio_mocha_fullwidth.png"
              alt="Emilio Mocha Brannan"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
        <StyledDesignColumn width={3} tablet={'four'} desktop={'four'} mobile="four">
          <ButtonNoneStyled
            id="DesignMinimalistArch"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick({ themeId: 'minimalist_arch' }, 'aloha')}
          >
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/minimalist_arch_aloha.png"
              alt="Minimalist Arch Aloha"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
        <StyledDesignColumn width={3}>
          <ButtonNoneStyled
            id="DesignTiffanyRed"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick({ title: 'Tiffany Red' }, 'brannan')}
          >
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/tiffany_red_fullwidth.png"
              alt="Tiffany Red Brannan"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
      </StyledDesignColumns>
      <Columns centered verticalAlign="flex-start" marginTop={[6, 10]} marginBottom={[6, 6]}>
        <StyledDesignTextImageColumn width={4}>
          <ButtonNoneStyled
            id="DesignNewsstandTaffy"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick({ title: 'Newsstand Taffy' }, 'brannan')}
          >
            <StyledTextPlaceholderCloudImage
              resizeWidth={355}
              isMainSrcShown={isVisible}
              src="wedding-website/design-gallery/newsstand_taffy_fullwidth.png"
              alt="Newsstand Taffy Brannan"
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignTextImageColumn>
        <StyledDesignTextColumn width={4}>
          <Box>
            <CarouselSlideDetails>
              <SlideDetailsText tagName={'h2'} variant={'hed56'} color={'neutra6'} marginBottom={6}>
                {ReactHtmlParser(sectionData.title)}
              </SlideDetailsText>
              <SlideDetailsBodyText tagName={'p'} variant={'dek20'} color={'neutra5'} marginBottom={[8, 6]}>
                {sectionData.content}
              </SlideDetailsBodyText>
              <CtaWrapper id="exploreDesignTemplates" forwardedAs={'a'} href="/designs">
                {sectionData.ctaText}
              </CtaWrapper>
              <StyledCTALink id="exploreDesignTemplatesLink" hasChevron={true} typography="featureHed17" href="/designs">
                {sectionData.ctaText}
              </StyledCTALink>
            </CarouselSlideDetails>
          </Box>
        </StyledDesignTextColumn>
        <StyledDesignTextImageColumn width={4}>
          <ButtonNoneStyled
            id="DesignRusticChic"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick({ themeId: 'rustic_chic' }, 'aloha')}
          >
            <StyledTextPlaceholderCloudImage
              resizeWidth={355}
              src="wedding-website/design-gallery/rustic_chic_aloha.png"
              alt="Rustic Chic Aloha"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignTextImageColumn>
      </Columns>
      <StyledDesignColumns stackOnMobile={false} centered verticalAlign="flex-start" marginTop={[6, 10]} marginBottom={[6, 12]}>
        <StyledDesignColumn width={3}>
          <ButtonNoneStyled
            id="DesignIvoryGarden"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick({ themeId: 'ivory_garden' }, 'brannan')}
          >
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/ivory_garden_brannan.png"
              alt="Ivory Garden Brannan"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
        <StyledDesignColumn width={3}>
          <ButtonNoneStyled
            id="DesignWavyPink"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick({ title: 'Wavy Pink' }, 'brannan')}
          >
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/wavy_pink_fullwidth.png"
              alt="Wavy Pink Brannan"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
        <StyledDesignColumn width={3}>
          <ButtonNoneStyled
            id="DesignFormalDeco"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick({ themeId: 'formal_deco' }, 'aloha')}
          >
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/formal_deco_aloha.png"
              alt="Formal Deco Aloha"
              width="330"
              height="100%"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
        <StyledDesignColumn width={3}>
          <ButtonNoneStyled
            id="DesignEditorialLime"
            skipRedirectWithTelemetry
            onClick={() => handleCardClick({ title: 'Editorial Lime' }, 'brannan')}
          >
            <StyledPlaceholderCloudImage
              resizeWidth={330}
              mobileResizeWidth={142}
              src="wedding-website/design-gallery/editorial_lime_fullwidth.png"
              alt="Editorial Lime Brannan"
              isMainSrcShown={isVisible}
              optimization="default"
            />
          </ButtonNoneStyled>
        </StyledDesignColumn>
      </StyledDesignColumns>

      {isDisplayCardDetails && (
        <GalleryECardModal
          productType={designs as GalleryProductType}
          handleClose={handleCloseModal}
          cardDetails={cardDetails}
          isModalOpen={isDisplayCardDetails}
          isWeddingDesignSlider={true}
        />
      )}
    </Box>
  );
};
